import { useEffect, useRef, useState } from "react";
import {
  ImageRequestDTO,
  SubmitUtilityBvnSelfieVerifyRequestDTO,
  UtilityImageDTO,
  VerificationService,
} from "@zenith/services/user";
import { SecurityVerificationStepUpStatus } from "./securityVerificationStepUp.types";

// BOAS-7938 — the unauthenticated (login-page) variant of the facial
// step-up flow. Reuses the existing public BVN-selfie utility endpoints
// (POST /api/utility/bvn-selfie/verify, GET .../status/{jobId}) keyed by
// phone number rather than a JWT; purpose=PASSCODE_RESET is what makes
// them behave as the passcode-reset submit/poll. Same 5-minute countdown
// and 30s reassurance banner as useSecurityVerificationSelfie, but a
// different response shape (selfieJobId, combinedStatus) so it isn't a
// drop-in reuse of that hook — mirrors its shape instead so both can
// drive the same FacialStepUpComponent.
const POLL_INTERVAL_MS = 5000;
const MAX_POLLING_TIME_MS = 5 * 60 * 1000;
const REASSURANCE_BANNER_DELAY_MS = 30 * 1000;

const SUCCESS_STATUSES = ["VERIFIED", "VERIFIED_SUCCESSFULLY"];
const FAILURE_STATUSES = [
  "ERROR_IN_VERIFICATION",
  "GENERAL_ERROR_IN_VERIFICATION",
  "NOT_VERIFIED",
  "NEEDS_MANUAL_VERIFICATION",
];

interface UsePublicFacialVerificationResult {
  status: SecurityVerificationStepUpStatus;
  jobId: string | null;
  attemptsRemaining: number | null;
  lockedUntil: string | null;
  errorMessage: string | null;
  showReassuranceBanner: boolean;
  secondsRemaining: number;
  submit: (phoneNumber: string, photos: ImageRequestDTO[]) => void;
  reset: () => void;
}

export const usePublicFacialVerification = (
  purpose: SubmitUtilityBvnSelfieVerifyRequestDTO.purpose
): UsePublicFacialVerificationResult => {
  const [status, setStatus] = useState<SecurityVerificationStepUpStatus>(
    "idle"
  );
  const [jobId, setJobId] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
    null
  );
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showReassuranceBanner, setShowReassuranceBanner] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    MAX_POLLING_TIME_MS / 1000
  );

  const pollTimeoutRef = useRef<NodeJS.Timeout>();
  const bannerTimeoutRef = useRef<NodeJS.Timeout>();
  const countdownIntervalRef = useRef<NodeJS.Timeout>();
  const startTimeRef = useRef(0);
  const isMountedRef = useRef(true);
  const activeJobIdRef = useRef<string | null>(null);

  const clearAllTimers = () => {
    clearTimeout(pollTimeoutRef.current);
    clearTimeout(bannerTimeoutRef.current);
    clearInterval(countdownIntervalRef.current);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearAllTimers();
    };
  }, []);

  const reset = () => {
    clearAllTimers();
    activeJobIdRef.current = null;
    setStatus("idle");
    setJobId(null);
    setAttemptsRemaining(null);
    setLockedUntil(null);
    setErrorMessage(null);
    setShowReassuranceBanner(false);
    setSecondsRemaining(MAX_POLLING_TIME_MS / 1000);
  };

  const schedulePoll = (activeJobId: string) => {
    pollTimeoutRef.current = setTimeout(() => poll(activeJobId), POLL_INTERVAL_MS);
  };

  const poll = (activeJobId: string) => {
    if (!isMountedRef.current || activeJobIdRef.current !== activeJobId) return;

    // selfieJobId is what the status endpoint expects, despite this
    // parameter being named jobId on the shared VerificationService.
    VerificationService.getVerificationStatus(activeJobId)
      .then((response) => {
        if (!isMountedRef.current || activeJobIdRef.current !== activeJobId) {
          return;
        }

        const data = response.data;

        if (typeof data?.attemptsRemaining === "number") {
          setAttemptsRemaining(data.attemptsRemaining);
        }

        if (data?.timedOut) {
          clearAllTimers();
          setStatus("timedOut");
          return;
        }

        const verificationStatus =
          data?.combinedStatus || data?.selfieStatus?.verificationStatus;

        if (verificationStatus && SUCCESS_STATUSES.includes(verificationStatus)) {
          clearAllTimers();
          setStatus("verified");
          return;
        }

        if (verificationStatus && FAILURE_STATUSES.includes(verificationStatus)) {
          clearAllTimers();
          setStatus("failed");
          setErrorMessage(
            data?.selfieStatus?.resultText ||
              "We could not complete your verification"
          );
          return;
        }

        if (Date.now() - startTimeRef.current >= MAX_POLLING_TIME_MS) {
          clearAllTimers();
          setStatus("timedOut");
          return;
        }

        schedulePoll(activeJobId);
      })
      .catch(() => {
        if (!isMountedRef.current || activeJobIdRef.current !== activeJobId) {
          return;
        }
        // A network hiccup tells us nothing about the verification's real
        // outcome - keep polling rather than failing the merchant out.
        schedulePoll(activeJobId);
      });
  };

  const startPolling = (activeJobId: string) => {
    setStatus("polling");
    startTimeRef.current = Date.now();
    setSecondsRemaining(MAX_POLLING_TIME_MS / 1000);

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setSecondsRemaining(
        Math.max(0, Math.ceil((MAX_POLLING_TIME_MS - elapsed) / 1000))
      );
    }, 1000);

    bannerTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) setShowReassuranceBanner(true);
    }, REASSURANCE_BANNER_DELAY_MS);

    poll(activeJobId);
  };

  const submit = (phoneNumber: string, photos: ImageRequestDTO[]) => {
    clearAllTimers();
    activeJobIdRef.current = null;
    setStatus("submitting");
    setErrorMessage(null);
    setShowReassuranceBanner(false);
    setAttemptsRemaining(null);

    const images: UtilityImageDTO[] = photos.map((photo) => ({
      imageContent: photo.imageContent || "",
      imageType: photo.imageType as unknown as UtilityImageDTO.imageType,
      mimeExt: photo.mimeExt || "jpeg",
    }));

    VerificationService.submitBvnSelfieVerification({
      phoneNumber,
      purpose: purpose as unknown as SubmitUtilityBvnSelfieVerifyRequestDTO.purpose,
      verificationType:
        SubmitUtilityBvnSelfieVerifyRequestDTO.verificationType
          .SELFIE_VERIFICATION,
      images,
      // bvnNumber intentionally omitted - resolved server-side from phone.
    })
      .then((response) => {
        if (!isMountedRef.current) return;

        const newJobId = response.data?.selfieJobId;
        if (!newJobId) {
          setStatus("error");
          setErrorMessage("We could not complete your verification");
          return;
        }

        activeJobIdRef.current = newJobId;
        setJobId(newJobId);
        startPolling(newJobId);
      })
      .catch((error: any) => {
        if (!isMountedRef.current) return;

        if (error?.status === 429) {
          setStatus("locked");
          setLockedUntil(error.body?.data?.lockedUntil ?? null);
          return;
        }

        if (error?.status === 409) {
          // Multi-signatory phone - facial isn't applicable; the caller
          // falls back to the OTP reset flow.
          setStatus("notEligible");
          return;
        }

        setStatus("error");
        setErrorMessage(
          error?.body?.message || "We could not complete your verification"
        );
      });
  };

  return {
    status,
    jobId,
    attemptsRemaining,
    lockedUntil,
    errorMessage,
    showReassuranceBanner,
    secondsRemaining,
    submit,
    reset,
  };
};
